import type {FC, HTMLAttributes} from 'react'
import type React from 'react'
import {GrabberIcon} from '@primer/octicons-react'
import {IconButton, Box} from '@primer/react'
import {useSortable} from '@dnd-kit/sortable'
import {useSortableItem} from '../hooks/use-sortable-item'
import {testIdProps} from '@github-ui/test-id-props'

export interface SortableListItemProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'id' | 'aria-labelledby'> {}

/**
 * The trigger button that activates the drag and drop of a sortable item. It is HIGHLY recommended to use
 * this component to trigger drag and drop since it includes additional accessibility features
 * to make the drag and drop experience work more robustly with screen readers.
 */
export const SortableListTrigger: FC<React.PropsWithChildren<SortableListItemProps>> = props => {
  const {title, id} = useSortableItem()
  const {setActivatorNodeRef, listeners, isDragging} = useSortable({
    id,
    data: {metadata: {id}},
  })

  const {onMouseDown, onClick, onKeyDown} = listeners as {
    onMouseDown: React.MouseEventHandler
    onClick: React.MouseEventHandler
    onKeyDown: React.KeyboardEventHandler
  }

  return (
    <Box className="sortable-trigger" sx={{display: 'flex', position: 'relative'}}>
      {/*
        Some screen readers (NVDA) simulate a click when the user presses enter or space, which
        triggers the drag and drop from a mouse source rather than a keyboard source, which
        prevents drag and drop from behaving correctly. This works around that confusion
        by having a clickable layer on top of the trigger button that handles the mouse event triggered
        by the actual mouse, whereas the button handles the mouse event triggered by the screen reader.
      */}
      <Box onMouseDown={onMouseDown} sx={{position: 'absolute', width: '100%', height: '100%', cursor: 'grab'}} />
      <IconButton
        sx={{p: 1, cursor: 'grab', mr: 1}}
        aria-label={`Move ${title}`}
        ref={setActivatorNodeRef}
        onKeyDown={onKeyDown}
        onClick={onClick}
        variant="invisible"
        // When dragging, the button is no longer a button, but has the role application to allow
        // keyboard movements to not be registered by the screen reader
        role={isDragging ? 'application' : 'button'}
        icon={GrabberIcon}
        size="large"
        {...props}
        {...testIdProps('sortable-trigger')}
      />
    </Box>
  )
}
